<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { mdiCheck } from '@mdi/js';
import PlayerAvatar from './PlayerAvatar.vue';
import PlayerIdentity from './PlayerIdentity.vue';
import { useSelection } from '../composables/useSelection.js';
import { LONG_PRESS_MS, movedTooFar } from '../domain/selection.js';

const props = defineProps({
  player: { type: Object, required: true },
  /* Nur Zeilen des Kaders sind markierbar (nicht Freigegeben); die Rolle prüft useSelection.enter */
  selectable: { type: Boolean, default: false },
});
const emit = defineEmits(['open']);

const { selecting, canSelect, isSelected, enter, toggle, select } = useSelection();

/* Zeile nimmt am Markieren teil: Rolle darf und die Liste erlaubt es */
const markable = () => props.selectable && canSelect.value;
const inMode = () => markable() && selecting.value;

/* Leerer touchstart-Handler: Ohne ihn wendet iOS Safari :active auf Zeilen nicht
   zuverlässig an (Klick-Rückmeldung in main.css). */
const noop = () => {};

/* ---------- Langes Drücken (Handy) ---------- */
let timer = null;
let startX = 0;
let startY = 0;
/* Nach dem langen Drücken folgt ein Klick; der darf nicht zusätzlich das Profil öffnen oder wieder abwählen */
const swallowClick = ref(false);

function cancelPress() {
  if (timer) clearTimeout(timer);
  timer = null;
}
function onPointerDown(e) {
  swallowClick.value = false;
  /* Maus: Rechtsklick (contextmenu) und der Select-Knopf genügen */
  if (!markable() || selecting.value || e.pointerType === 'mouse') return;
  startX = e.clientX;
  startY = e.clientY;
  cancelPress();
  timer = setTimeout(() => {
    timer = null;
    if (enter(props.player.id)) swallowClick.value = true;
  }, LONG_PRESS_MS);
}
function onPointerMove(e) {
  if (timer && movedTooFar(e.clientX - startX, e.clientY - startY)) cancelPress();
}
onBeforeUnmount(cancelPress);

/* Rechtsklick (und das Kontextmenü, das Android nach langem Drücken auslöst): Modus an, Profil markiert.
   Ohne Recht bleibt das Browser-Menü. */
function onContextMenu(e) {
  if (!markable()) return;
  e.preventDefault();
  if (enter(props.player.id)) select(props.player.id);
}

function activate() {
  if (swallowClick.value) {
    swallowClick.value = false;
    return;
  }
  if (inMode()) toggle(props.player.id);
  else emit('open', props.player.id);
}
</script>

<template>
  <div
    class="row"
    :class="{ selectable: markable(), 'is-selecting': inMode(), 'is-selected': inMode() && isSelected(player.id) }"
    role="button"
    tabindex="0"
    :aria-pressed="inMode() ? isSelected(player.id) : undefined"
    @keydown.enter.self.prevent="activate"
    @keydown.space.self.prevent="activate"
    @touchstart.passive="noop"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="cancelPress"
    @pointercancel="cancelPress"
    @pointerleave="cancelPress"
    @contextmenu="onContextMenu"
    @click="activate"
  >
    <PlayerAvatar :player="player" :size="40" />
    <!-- Zeichen für "antippbar" (leerer Kreis) bzw. "markiert" (Häkchen); nie nur über Farbe -->
    <span v-if="inMode()" class="sel-mark" :class="{ on: isSelected(player.id) }" aria-hidden="true">
      <v-icon v-if="isSelected(player.id)" :icon="mdiCheck" size="14" />
    </span>
    <span class="body">
      <PlayerIdentity :player="player" />
    </span>
  </div>
</template>

<style scoped>
.row.selectable {
  position: relative;
  /* iOS: kein Textauswahl-Lupe und kein Kontextmenü beim langen Drücken auf die Zeile */
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}
.row.is-selected {
  background: color-mix(in srgb, var(--text) 8%, var(--card));
  border-left-color: var(--green);
}
.sel-mark {
  position: absolute;
  left: 34px;
  top: calc(50% + 6px);
  width: 20px;
  height: 20px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid var(--muted);
  background: var(--card);
  pointer-events: none;
}
.sel-mark.on {
  border-color: var(--green);
  background: var(--green);
  color: var(--bg);
}
</style>
